import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { User, Menu } from "lucide-react";
import logoImage from "/favicon.png";
import { Button } from "@/components/ui/button";
import { CartDrawer } from "@/components/CartDrawer";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { supabase } from "@/integrations/supabase/client";
import type { User as SupabaseUser } from "@supabase/supabase-js";

interface NavigationProps {
  showCustomDesignLink?: boolean;
}

export const Navigation = ({ showCustomDesignLink = true }: NavigationProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [user, setUser] = useState<SupabaseUser | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);


  return (
    <header className="sticky top-0 z-50 w-full bg-background border-b">
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src={logoImage} alt="NailsPressOn logo" className="h-8 w-8" />
          <span className="text-xl font-bold tracking-tight uppercase">nailspresson</span>
        </Link>
        
        <nav className="hidden md:flex items-center gap-8">
          <Link to="/" className="text-sm hover:text-primary transition-colors">Products</Link>
          {showCustomDesignLink && (
            <Link to="/ai-design" className="text-sm hover:text-primary transition-colors">
              AI Design
            </Link>
          )}
          <Link to="/size-chart" className="text-sm hover:text-primary transition-colors">Size Chart</Link>
          <Link to="/blog" className="text-sm hover:text-primary transition-colors">Blog</Link>
          <Link to="/faq" className="text-sm hover:text-primary transition-colors">FAQ</Link>
          <Link to="/contact" className="text-sm hover:text-primary transition-colors">Contact</Link>
        </nav>

        <div className="flex items-center gap-4">
          <CartDrawer />
          {user ? (
            <Button variant="ghost" asChild className="gap-2">
              <Link to="/dashboard">
                <User className="h-5 w-5" />
              </Link>
            </Button>
          ) : (
            <Button variant="ghost" asChild className="gap-2">
              <Link to="/auth">
                <User className="h-5 w-5" />
              </Link>
            </Button>
          )}

          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right">
              <nav className="flex flex-col gap-6 mt-8">
                <Link 
                  to="/" 
                  className="text-lg hover:text-primary transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Products
                </Link>
                {showCustomDesignLink && (
                  <Link 
                    to="/ai-design"
                    className="text-lg hover:text-primary transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    AI Design
                  </Link>
                )}
                <Link 
                  to="/size-chart" 
                  className="text-lg hover:text-primary transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Size Chart
                </Link>
                <Link 
                  to="/blog" 
                  className="text-lg hover:text-primary transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Blog
                </Link>
                <Link 
                  to="/faq" 
                  className="text-lg hover:text-primary transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  FAQ
                </Link>
                <Link 
                  to="/contact" 
                  className="text-lg hover:text-primary transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contact
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
